import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import DocumentTitle from './DocumentTitle';

const LandingPage = lazy(() => import('./components/LandingPage'));
const ChatPage = lazy(() => import('./components/ChatPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const ServicesPage = lazy(() => import('./components/ServicesPage'));
const ServiceAiWebsitesPage = lazy(() => import('./components/ServiceAiWebsitesPage'));
const ServiceAiChatbotsPage = lazy(() => import('./components/ServiceAiChatbotsPage'));
// Lazy chunk for /services/custom-llm-development (used in <Route> below)
const LazyServiceAiLlmPage = lazy(() => import('./components/ServiceAiLlmPage'));
const LazyServiceB2bEnterpriseIntegrationsPage = lazy(() => import('./components/ServiceB2bEnterpriseIntegrationsPage'));
const LazyServiceAiPersonalizedLearningPage = lazy(() => import('./components/ServiceAiPersonalizedLearningPage'));
const LazyServiceAiotPage = lazy(() => import('./components/ServiceAiotPage'));

// Blog system
const LazyBlogListPage = lazy(() => import('./components/BlogListPage'));
const LazyBlogPostPage = lazy(() => import('./components/BlogPostPage'));

// Case studies
const LazyCaseStudyPage = lazy(() => import('./components/CaseStudyPage'));

// 404 page
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));

// AI Chatbot cluster pages (industry-specific, hidden from menu, linked from pillar)
const ClusterAiChatbotEcommerce = lazy(() => import('./components/clusters/AiChatbotEcommerceHyderabad'));
const ClusterAiChatbotHealthcare = lazy(() => import('./components/clusters/AiChatbotHealthcareHyderabad'));
const ClusterAiChatbotRealEstate = lazy(() => import('./components/clusters/AiChatbotRealEstateHyderabad'));
const ClusterAiChatbotStartups = lazy(() => import('./components/clusters/AiChatbotStartupsIndia'));
const ClusterAiChatbotEducation = lazy(() => import('./components/clusters/AiChatbotEducationHyderabad'));

// AI Enhanced Websites cluster pages
const ClusterAiWebsitesEcommerce = lazy(() => import('./components/clusters/AiWebsitesEcommerceHyderabad'));
const ClusterAiWebsitesRealEstate = lazy(() => import('./components/clusters/AiWebsitesRealEstateHyderabad'));
const ClusterAiWebsitesHealthcare = lazy(() => import('./components/clusters/AiWebsitesHealthcareHyderabad'));
const ClusterAiWebsitesStartups = lazy(() => import('./components/clusters/AiWebsitesStartupsIndia'));
const ClusterAiWebsitesEducation = lazy(() => import('./components/clusters/AiWebsitesEducationHyderabad'));

// Custom LLM Development cluster pages
const ClusterCustomLlmLegal = lazy(() => import('./components/clusters/CustomLlmLegalIndia'));
const ClusterCustomLlmHealthcare = lazy(() => import('./components/clusters/CustomLlmHealthcareHyderabad'));
const ClusterCustomLlmFintech = lazy(() => import('./components/clusters/CustomLlmFintechIndia'));
const ClusterCustomLlmEnterprise = lazy(() => import('./components/clusters/CustomLlmEnterpriseHyderabad'));
const ClusterCustomLlmResearch = lazy(() => import('./components/clusters/CustomLlmResearchIndia'));

// B2B AI Enterprise Integrations cluster pages
const ClusterB2bAiManufacturing = lazy(() => import('./components/clusters/B2bAiManufacturingHyderabad'));
const ClusterB2bAiLogistics = lazy(() => import('./components/clusters/B2bAiLogisticsIndia'));
const ClusterB2bAiRetail = lazy(() => import('./components/clusters/B2bAiRetailHyderabad'));
const ClusterB2bAiFintech = lazy(() => import('./components/clusters/B2bAiFintechIndia'));
const ClusterB2bAiSaas = lazy(() => import('./components/clusters/B2bAiSaasIndia'));

// AI Personalized Learning cluster pages
const ClusterAiLearningSchools = lazy(() => import('./components/clusters/AiLearningSchoolsTelangana'));
const ClusterAiLearningColleges = lazy(() => import('./components/clusters/AiLearningCollegesHyderabad'));
const ClusterAiLearningEdtech = lazy(() => import('./components/clusters/AiLearningEdtechIndia'));
const ClusterAiLearningCorporate = lazy(() => import('./components/clusters/AiLearningCorporateIndia'));
const ClusterAiLearningCoaching = lazy(() => import('./components/clusters/AiLearningCoachingHyderabad'));

// AIoT (AI + IoT) cluster pages
const ClusterAiotManufacturing = lazy(() => import('./components/clusters/AiotManufacturingHyderabad'));
const ClusterAiotAgriculture = lazy(() => import('./components/clusters/AiotAgricultureIndia'));
const ClusterAiotSmartCities = lazy(() => import('./components/clusters/AiotSmartCitiesHyderabad'));
const ClusterAiotEducation = lazy(() => import('./components/clusters/AiotEducationTelangana'));
const ClusterAiotHealthcare = lazy(() => import('./components/clusters/AiotHealthcareHyderabad'));
const HoverCard = lazy(() => import('./components/HoverCard'));
const ProjectsPage = lazy(() => import('./components/ProjectsPage'));
const ProjectsPagePro = lazy(() => import('./components/ProjectsPagePro'));
const NewNavBar = lazy(() => import('./components/NewNavBar'));
const VisionPage = lazy(() => import('./components/VisionPage'));
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./components/CookiePolicy'));
const RefundPolicy = lazy(() => import('./components/RefundPolicy'));


function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
    <Router>
      <ScrollToTop />
      <DocumentTitle />
      <Suspense fallback={<div></div>}>
        <Routes>
          <Route path="/12" element={<HoverCard />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="/Nav" element={<NewNavBar />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/ai-enhanced-websites" element={<ServiceAiWebsitesPage />} />
          <Route path="/services/ai-chatbots-virtual-assistants" element={<ServiceAiChatbotsPage />} />
          <Route path="/services/custom-llm-development" element={<LazyServiceAiLlmPage />} />
          <Route
            path="/services/b2b-ai-enterprise-integrations"
            element={<LazyServiceB2bEnterpriseIntegrationsPage />}
          />
          <Route
            path="/services/ai-personalized-learning"
            element={<LazyServiceAiPersonalizedLearningPage />}
          />
          <Route
            path="/services/ai-iot-automation"
            element={<LazyServiceAiotPage />}
          />
          {/* Hyderabad keyword-primary alias routes (CTO doc rule 07 — keyword in URL).
              Each alias renders the same component as the canonical pillar URL. */}
          <Route path="/services/ai-chatbot-hyderabad" element={<ServiceAiChatbotsPage />} />
          <Route path="/services/ai-website-hyderabad" element={<ServiceAiWebsitesPage />} />
          <Route path="/services/custom-llm-hyderabad" element={<LazyServiceAiLlmPage />} />
          <Route path="/services/b2b-ai-hyderabad" element={<LazyServiceB2bEnterpriseIntegrationsPage />} />
          <Route path="/services/ai-learning-hyderabad" element={<LazyServiceAiPersonalizedLearningPage />} />
          <Route path="/services/aiot-hyderabad" element={<LazyServiceAiotPage />} />
          {/* AI Chatbot cluster pages — linked from pillar, hidden from nav */}
          <Route path="/services/ai-chatbot-ecommerce-hyderabad" element={<ClusterAiChatbotEcommerce />} />
          <Route path="/services/ai-chatbot-healthcare-hyderabad" element={<ClusterAiChatbotHealthcare />} />
          <Route path="/services/ai-chatbot-real-estate-hyderabad" element={<ClusterAiChatbotRealEstate />} />
          <Route path="/services/ai-chatbot-startups-india" element={<ClusterAiChatbotStartups />} />
          <Route path="/services/ai-chatbot-education-hyderabad" element={<ClusterAiChatbotEducation />} />
          {/* AI Enhanced Websites cluster pages */}
          <Route path="/services/ai-websites-ecommerce-hyderabad" element={<ClusterAiWebsitesEcommerce />} />
          <Route path="/services/ai-websites-real-estate-hyderabad" element={<ClusterAiWebsitesRealEstate />} />
          <Route path="/services/ai-websites-healthcare-hyderabad" element={<ClusterAiWebsitesHealthcare />} />
          <Route path="/services/ai-websites-startups-india" element={<ClusterAiWebsitesStartups />} />
          <Route path="/services/ai-websites-education-hyderabad" element={<ClusterAiWebsitesEducation />} />
          {/* Custom LLM Development cluster pages */}
          <Route path="/services/custom-llm-legal-india" element={<ClusterCustomLlmLegal />} />
          <Route path="/services/custom-llm-healthcare-hyderabad" element={<ClusterCustomLlmHealthcare />} />
          <Route path="/services/custom-llm-fintech-india" element={<ClusterCustomLlmFintech />} />
          <Route path="/services/custom-llm-enterprise-hyderabad" element={<ClusterCustomLlmEnterprise />} />
          <Route path="/services/custom-llm-research-india" element={<ClusterCustomLlmResearch />} />
          {/* B2B AI Enterprise Integrations cluster pages */}
          <Route path="/services/b2b-ai-manufacturing-hyderabad" element={<ClusterB2bAiManufacturing />} />
          <Route path="/services/b2b-ai-logistics-india" element={<ClusterB2bAiLogistics />} />
          <Route path="/services/b2b-ai-retail-hyderabad" element={<ClusterB2bAiRetail />} />
          <Route path="/services/b2b-ai-fintech-india" element={<ClusterB2bAiFintech />} />
          <Route path="/services/b2b-ai-saas-india" element={<ClusterB2bAiSaas />} />
          {/* AI Personalized Learning cluster pages */}
          <Route path="/services/ai-learning-schools-telangana" element={<ClusterAiLearningSchools />} />
          <Route path="/services/ai-learning-colleges-hyderabad" element={<ClusterAiLearningColleges />} />
          <Route path="/services/ai-learning-edtech-india" element={<ClusterAiLearningEdtech />} />
          <Route path="/services/ai-learning-corporate-india" element={<ClusterAiLearningCorporate />} />
          <Route path="/services/ai-learning-coaching-hyderabad" element={<ClusterAiLearningCoaching />} />
          {/* AIoT (AI + IoT) cluster pages */}
          <Route path="/services/aiot-manufacturing-hyderabad" element={<ClusterAiotManufacturing />} />
          <Route path="/services/aiot-agriculture-india" element={<ClusterAiotAgriculture />} />
          <Route path="/services/aiot-smart-cities-hyderabad" element={<ClusterAiotSmartCities />} />
          <Route path="/services/aiot-education-telangana" element={<ClusterAiotEducation />} />
          <Route path="/services/aiot-healthcare-hyderabad" element={<ClusterAiotHealthcare />} />
          {/* Blog routes — completely separate from /services/ (CTO doc rule) */}
          <Route path="/blog" element={<LazyBlogListPage />} />
          <Route path="/blog/:slug" element={<LazyBlogPostPage />} />
          {/* Case study route */}
          <Route path="/case-studies/:slug" element={<LazyCaseStudyPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects-pro" element={<ProjectsPagePro />} />
          <Route path="/vision" element={<VisionPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          {/* Legal & Policy Pages */}
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          {/* Catch-all: any unknown route shows 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Router>
    </HelmetProvider>
  );
}

export default App;
